import { cart } from '../helpers/cart'
import { CartRequestItem } from '../helpers/cart.types'

/**
 * CustomisationForm - Custom HTML Element for handling customisation product forms
 *
 * This element manages the customisation modal and handles adding both the main product
 * and the customisation product to the cart simultaneously.
 */
export class CustomisationForm extends HTMLElement {
  private mainProductForm: HTMLFormElement | null = null
  private customisationProductForm: HTMLFormElement | null = null
  private addToBagButton: HTMLButtonElement | null = null
  private customisationTextInput: HTMLInputElement | null = null
  private variantSelector: HTMLSelectElement | null = null
  private errorDisplay: HTMLElement | null = null

  private config = {
    mainProductId: '',
    customisationProductId: '',
    maxCharacters: 0
  }

  private selectors = {
    mainProductForm: '[data-customisation-main-product]',
    variantSelector: 'select[name="id"]',
    customisationProductForm: '[data-customisation-custom-product]',
    customisationTextInput: '#customisation-text',
    addToBagButton: '[data-customisation-button]',
    errorDisplay: '[data-customisation-error]'
  }

  constructor() {
    super()
    this.initConfig()
    this.initUIElements()
    this.addEventListeners()
  }

  private initConfig() {
    const mainForm = this.querySelector(this.selectors.mainProductForm) as HTMLFormElement
    if (mainForm) {
      const variantSelector = mainForm.querySelector('select[name="id"]') as HTMLSelectElement
      this.config.mainProductId = variantSelector?.value || ''
    }

    const customisationForm = this.querySelector(this.selectors.customisationProductForm) as HTMLFormElement
    if (customisationForm) {
      const customisationProductInput = customisationForm.querySelector('input[name="id"]') as HTMLInputElement
      this.config.customisationProductId = customisationProductInput?.value || ''
    }

    const textInput = this.querySelector(this.selectors.customisationTextInput) as HTMLInputElement
    if (textInput) {
      this.config.maxCharacters = parseInt(textInput.getAttribute('maxlength') || '0')
    }
  }

  private initUIElements() {
    this.mainProductForm = this.querySelector(this.selectors.mainProductForm) as HTMLFormElement
    if (this.mainProductForm) {
      this.variantSelector = this.mainProductForm.querySelector(this.selectors.variantSelector) as HTMLSelectElement
    }
    this.customisationProductForm = this.querySelector(this.selectors.customisationProductForm) as HTMLFormElement
    this.customisationTextInput = this.querySelector(this.selectors.customisationTextInput) as HTMLInputElement
    this.addToBagButton = this.querySelector(this.selectors.addToBagButton) as HTMLButtonElement
    this.errorDisplay = this.querySelector(this.selectors.errorDisplay) as HTMLElement
  }

  private addEventListeners() {
    if (this.addToBagButton) {
      this.addToBagButton.addEventListener('click', this.handleAddToBag.bind(this))
    }

    if (this.variantSelector) {
      this.variantSelector.addEventListener('change', this.handleVariantChange.bind(this))
    }

    if (this.customisationTextInput) {
      this.customisationTextInput.addEventListener('input', this.handleTextInput.bind(this))
    }
  }

  private handleVariantChange() {
    if (this.variantSelector && this.mainProductForm) {
      const selectedVariantId = this.variantSelector.value
      this.config.mainProductId = selectedVariantId
    }
  }

  private handleTextInput() {
    if (this.customisationTextInput && this.config.maxCharacters > 0) {
      const filteredValue = this.customisationTextInput.value.replace(/[^a-zA-Z\s]/g, '')

      if (filteredValue !== this.customisationTextInput.value) {
        this.customisationTextInput.value = filteredValue
      }

      const currentLength = filteredValue.length
      const isValid = currentLength >= 1 && currentLength <= this.config.maxCharacters

      if (this.addToBagButton) {
        this.addToBagButton.disabled = !isValid
        this.addToBagButton.classList.toggle('btn-disabled', !isValid)
      }
    }
  }

  private generateGroupId(): string {
    const mainId = this.config.mainProductId || '0'
    const customId = this.config.customisationProductId || '0'
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
    return `${mainId}-${customId}-${random}`
  }

  private async handleAddToBag(event: Event) {
    event.preventDefault()

    if (!this.validateForm()) {
      return
    }

    if (this.addToBagButton) {
      this.addToBagButton.disabled = true
      this.addToBagButton.textContent = 'Adding...'
    }

    try {
      const items: CartRequestItem[] = []
      const customisationGroupId = this.generateGroupId()

      // Add customisation product
      if (this.config.customisationProductId) {
        const customisationProductId = parseInt(this.config.customisationProductId)
        if (isNaN(customisationProductId)) {
          this.showError('Invalid customisation product.')
          return
        }

        const customisationProperties: { [key: string]: string } = {
          _customProduct: 'true',
          _associatedProductId: this.config.mainProductId,
          _customisationGroupId: customisationGroupId
        }

        if (this.customisationTextInput && this.customisationTextInput.value.trim()) {
          customisationProperties['Customisation Text'] = this.customisationTextInput.value.trim()
        }

        const customisationItem: CartRequestItem = {
          id: customisationProductId,
          quantity: 1,
          properties: customisationProperties
        }
        items.push(customisationItem)
      } else {
        this.showError('Customisation product not found.')
        return
      }

      // Add main product
      if (this.config.mainProductId) {
        const mainProductId = parseInt(this.config.mainProductId)
        if (isNaN(mainProductId)) {
          this.showError('Invalid product selection.')
          return
        }

        const mainProductItem: CartRequestItem = {
          id: mainProductId,
          quantity: 1,
          properties: {
            _hasCustomisation: 'true',
            _customisationGroupId: customisationGroupId
          }
        }
        items.push(mainProductItem)
      } else {
        this.showError('Please select a size.')
        return
      }

      // Add items to cart
      if (items.length > 0) {
        try {
          const result = await cart.addLineItems(items, { silent: false })
          if (result && !result.error) {
            this.closeModal()
          } else {
            throw new Error(result?.error || 'Cart addition failed - no result returned')
          }
        } catch (cartError) {
          throw new Error(`Cart API error: ${cartError instanceof Error ? cartError.message : 'Unknown cart error'}`)
        }
      } else {
        throw new Error('No items to add to cart')
      }
    } catch (error) {
      this.showError(`Failed to add items to cart: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      if (this.addToBagButton) {
        this.addToBagButton.disabled = false
        this.addToBagButton.textContent = 'Add To Bag'
      }
    }
  }

  private validateForm(): boolean {
    if (this.customisationTextInput && this.config.maxCharacters > 0) {
      const text = this.customisationTextInput.value.trim()
      if (text.length < 1) {
        this.showError('Please enter customisation text.')
        return false
      }
    }

    return true
  }

  private closeModal() {
    const modal = this.closest('x-modal') as any
    if (modal && modal.handleHide) {
      modal.handleHide()
    }
  }

  private showError(message: string) {
    if (this.errorDisplay) {
      this.errorDisplay.innerHTML = `${message}`
    }

    if (this.addToBagButton) {
      this.addToBagButton.disabled = false
      this.addToBagButton.textContent = 'Add To Bag'
    }
  }
}
